/**
 * Project store - manages project state
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { projectsApi, type Project, type ProjectMember, type ProjectStats } from '@services/api';

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return error.message;
  return fallback;
};

interface ProjectState {
  // State
  projects: Project[];
  currentProject: Project | null;
  projectMembers: ProjectMember[];
  projectStats: ProjectStats | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchProjects: (organizationId: string) => Promise<void>;
  fetchProject: (projectId: string) => Promise<void>;
  createProject: (data: {
    organizationId: string;
    name: string;
    key: string;
    description?: string;
    isPublic?: boolean;
  }) => Promise<Project>;
  updateProject: (projectId: string, data: Partial<Project>) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  archiveProject: (projectId: string) => Promise<void>;
  restoreProject: (projectId: string) => Promise<void>;
  fetchProjectStats: (projectId: string) => Promise<void>;
  fetchProjectMembers: (projectId: string) => Promise<void>;
  addProjectMember: (
    projectId: string,
    userId: string,
    role: 'admin' | 'member' | 'viewer'
  ) => Promise<void>;
  updateMemberRole: (projectId: string, userId: string, role: string) => Promise<void>;
  removeProjectMember: (projectId: string, userId: string) => Promise<void>;
  setCurrentProject: (project: Project | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

export const useProjectStore = create<ProjectState>()(
  immer((set, get) => ({
    // Initial state
    projects: [],
    currentProject: null,
    projectMembers: [],
    projectStats: null,
    isLoading: false,
    error: null,

    // Actions
    fetchProjects: async (organizationId) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const response = await projectsApi.list(organizationId);
        set((state) => {
          state.projects = response.items;
          state.isLoading = false;
        });
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to fetch projects');
          state.isLoading = false;
        });
        throw error;
      }
    },

    fetchProject: async (projectId) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const project = await projectsApi.get(projectId);
        set((state) => {
          state.currentProject = project;
          state.isLoading = false;
        });
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to fetch project');
          state.isLoading = false;
        });
        throw error;
      }
    },

    createProject: async (data) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const project = await projectsApi.create(data);
        set((state) => {
          state.projects.push(project);
          state.isLoading = false;
        });
        return project;
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to create project');
          state.isLoading = false;
        });
        throw error;
      }
    },

    updateProject: async (projectId, data) => {
      const originalProject = get().projects.find((p: Project) => p.id === projectId);
      const originalCurrent = get().currentProject;

      // Optimistic update
      set((state) => {
        const index = state.projects.findIndex((p: Project) => p.id === projectId);
        if (index !== -1) {
          const project = state.projects[index];
          if (project) {
            Object.assign(project, data);
          }
        }
        if (state.currentProject?.id === projectId) {
          Object.assign(state.currentProject, data);
        }
      });

      try {
        // Filter out properties that aren't valid for update
        const { status: _status, ...updateData } = data;
        const updated = await projectsApi.update(projectId, updateData);
        set((state) => {
          const index = state.projects.findIndex((p: Project) => p.id === projectId);
          if (index !== -1) {
            state.projects[index] = updated;
          }
          if (state.currentProject?.id === projectId) {
            state.currentProject = updated;
          }
        });
      } catch (error: unknown) {
        // Rollback on error
        set((state) => {
          if (originalProject) {
            const index = state.projects.findIndex((p: Project) => p.id === projectId);
            if (index !== -1) {
              state.projects[index] = originalProject;
            }
          }
          if (originalCurrent) {
            state.currentProject = originalCurrent;
          }
          state.error = getErrorMessage(error, 'Failed to update project');
        });
        throw error;
      }
    },

    deleteProject: async (projectId) => {
      const originalProjects = [...get().projects];

      // Optimistic update
      set((state) => {
        state.projects = state.projects.filter((p: Project) => p.id !== projectId);
        if (state.currentProject?.id === projectId) {
          state.currentProject = null;
        }
      });

      try {
        await projectsApi.delete(projectId);
      } catch (error: unknown) {
        // Rollback on error
        set((state) => {
          state.projects = originalProjects;
          state.error = getErrorMessage(error, 'Failed to delete project');
        });
        throw error;
      }
    },

    archiveProject: async (projectId) => {
      try {
        const archived = await projectsApi.archive(projectId);
        set((state) => {
          const index = state.projects.findIndex((p: Project) => p.id === projectId);
          if (index !== -1) {
            state.projects[index] = archived;
          }
          if (state.currentProject?.id === projectId) {
            state.currentProject = archived;
          }
        });
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to archive project');
        });
        throw error;
      }
    },

    restoreProject: async (projectId) => {
      try {
        const restored = await projectsApi.restore(projectId);
        set((state) => {
          const index = state.projects.findIndex((p: Project) => p.id === projectId);
          if (index !== -1) {
            state.projects[index] = restored;
          }
          if (state.currentProject?.id === projectId) {
            state.currentProject = restored;
          }
        });
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to restore project');
        });
        throw error;
      }
    },

    fetchProjectStats: async (projectId) => {
      try {
        const stats = await projectsApi.getStats(projectId);
        set((state) => {
          state.projectStats = stats;
        });
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to fetch project stats');
        });
        throw error;
      }
    },

    fetchProjectMembers: async (projectId) => {
      try {
        const members = await projectsApi.listMembers(projectId);
        set((state) => {
          state.projectMembers = members;
        });
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to fetch project members');
        });
        throw error;
      }
    },

    addProjectMember: async (projectId, userId, role) => {
      try {
        const member = await projectsApi.addMember(projectId, { userId, role });
        set((state) => {
          state.projectMembers.push(member);
        });
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to add project member');
        });
        throw error;
      }
    },

    updateMemberRole: async (projectId, userId, role) => {
      try {
        const updated = await projectsApi.updateMemberRole(projectId, userId, role);
        set((state) => {
          const index = state.projectMembers.findIndex(
            (m: ProjectMember) => m.user.id === userId
          );
          if (index !== -1) {
            state.projectMembers[index] = updated;
          }
        });
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to update member role');
        });
        throw error;
      }
    },

    removeProjectMember: async (projectId, userId) => {
      const originalMembers = [...get().projectMembers];

      // Optimistic update
      set((state) => {
        state.projectMembers = state.projectMembers.filter(
          (m: ProjectMember) => m.user.id !== userId
        );
      });

      try {
        await projectsApi.removeMember(projectId, userId);
      } catch (error: unknown) {
        // Rollback on error
        set((state) => {
          state.projectMembers = originalMembers;
          state.error = getErrorMessage(error, 'Failed to remove project member');
        });
        throw error;
      }
    },

    setCurrentProject: (project) =>
      set((state) => {
        state.currentProject = project;
      }),

    setLoading: (isLoading) =>
      set((state) => {
        state.isLoading = isLoading;
      }),

    setError: (error) =>
      set((state) => {
        state.error = error;
      }),

    reset: () =>
      set((state) => {
        state.projects = [];
        state.currentProject = null;
        state.projectMembers = [];
        state.projectStats = null;
        state.isLoading = false;
        state.error = null;
      }),
  }))
);
